import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Ticket as TicketIcon } from 'lucide-react';
import Modal from './ui/Modal';
import MarkdownText from './ui/MarkdownText';
import apiService from '../services/apiService';
import { useI18n } from '../utils/i18n';
import { useToast } from './ToastProvider';
import '../styles/AiSearch.css';

// #123 (Welle 4.5): „Ähnliche Vorfälle" — KI-Suche direkt aus dem Ticket.
// Beim Öffnen läuft GET /api/tickets/:id/ai-similar (Suchseed = Titel +
// Beschreibung, eigenes Ticket serverseitig ausgefiltert); Rückfragen gehen
// wie in der KI-Suche über /api/search/ai-followup gegen das zurückgegebene
// Kontextfenster. „Ins Ticket übernehmen" speichert den Verlauf als internen
// Kommentar über die bestehende Kommentar-Route.
// #231: Props onOpenTicket(ticketId) — Treffer on-top öffnen (der Aufrufer rendert das
// Ziel-Ticket über diesem Modal, das Modal bleibt offen); lockClose sperrt ESC/X/Overlay,
// solange ein Ticket darüber liegt (der ui/Modal-ESC-Handler würde sonst beide schließen).
// Ohne onOpenTicket: Alt-Verhalten (globales Ticket-Modal + Schließen).
const TicketAiSearchModal = ({ ticket, open, onClose, onAdopted, onOpenTicket = null, lockClose = false }) => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]); // [{role:'user'|'assistant', content}]
  const [contextText, setContextText] = useState('');
  const [cards, setCards] = useState([]);
  const [composer, setComposer] = useState('');
  const [followupLoading, setFollowupLoading] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [error, setError] = useState(null);
  const threadEndRef = useRef(null);
  const requestedForRef = useRef(null);

  const runInitialSearch = useCallback(async () => {
    if (!ticket?.id) return;
    setLoading(true);
    setError(null);
    setMessages([]);
    setContextText('');
    setCards([]);
    try {
      const res = await apiService.ticketAiSimilar(ticket.id);
      setContextText(res?.contextText || '');
      setCards(Array.isArray(res?.cards) ? res.cards : []);
      setMessages([{
        role: 'assistant',
        content: res?.answer || t('search.noAnswer', 'Keine Antwort erhalten.'),
        ts: Date.now()
      }]);
    } catch (e) {
      if (e?.response?.data?.aiDisabled) {
        setError(t('search.aiDisabled', 'Die KI-Suche ist deaktiviert. Ein Administrator kann sie im Admin-Panel (Tab „AI Integration") aktivieren.'));
      } else {
        setError(t('aiSimilar.failed', 'KI-Suche fehlgeschlagen.'));
      }
    } finally {
      setLoading(false);
    }
  }, [ticket?.id, t]);

  // Einmal pro Öffnen (und pro Ticket) automatisch suchen
  useEffect(() => {
    if (!open) {
      requestedForRef.current = null;
      return;
    }
    if (ticket?.id && requestedForRef.current !== ticket.id) {
      requestedForRef.current = ticket.id;
      runInitialSearch();
    }
  }, [open, ticket?.id, runInitialSearch]);

  useEffect(() => {
    try { threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
  }, [messages, loading, followupLoading]);

  const sendFollowup = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const q = composer.trim();
    if (!q || loading || followupLoading || !contextText) return;
    setFollowupLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: q, ts: Date.now() }]);
    setComposer('');
    try {
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
      const res = await apiService.aiFollowup({
        followup: q,
        originalQuestion: `${t('aiSimilar.title', 'Ähnliche Vorfälle')}: ${ticket?.title || ''}`,
        contextText,
        previousAnswer: lastAssistant?.content || ''
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res?.answer || t('search.noAnswer', 'Keine Antwort erhalten.'), ts: Date.now() }]);
    } catch (_) {
      pushToast({ message: t('search.followupFailed', 'Rückfrage fehlgeschlagen.'), type: 'error' });
    } finally {
      setFollowupLoading(false);
    }
  }, [composer, loading, followupLoading, contextText, messages, ticket?.title, pushToast, t]);

  const adoptIntoTicket = useCallback(async () => {
    if (adopting || messages.length === 0 || !ticket?.id) return;
    setAdopting(true);
    try {
      const lines = [t('aiSimilar.commentTitle', 'KI-Suche: Ähnliche Vorfälle')];
      for (const m of messages) {
        lines.push('', `${m.role === 'user' ? t('search.roleQuestion', 'Frage') : t('search.roleAnswer', 'Antwort')}: ${m.content}`);
      }
      await apiService.addCommentToTicket(ticket.id, lines.join('\n'), true);
      pushToast({ message: t('aiSimilar.adopted', 'Als interner Kommentar gespeichert.'), type: 'success' });
      if (typeof onAdopted === 'function') onAdopted();
      if (typeof onClose === 'function') onClose();
    } catch (_) {
      pushToast({ message: t('aiSimilar.adoptFailed', 'Übernehmen fehlgeschlagen.'), type: 'error' });
    }
    setAdopting(false);
  }, [adopting, messages, ticket?.id, pushToast, t, onAdopted, onClose]);

  const openSimilarTicket = useCallback((ticketId) => {
    if (typeof onOpenTicket === 'function') {
      onOpenTicket(ticketId);
      return;
    }
    try {
      window.dispatchEvent(new CustomEvent('openTicketDetailById', { detail: { ticketId } }));
    } catch (_) {}
    if (typeof onClose === 'function') onClose();
  }, [onClose, onOpenTicket]);

  const hasAssistantMessage = messages.some(m => m.role === 'assistant');

  return (
    <Modal
      open={open}
      onClose={() => { if (!adopting && !lockClose) onClose(); }}
      title={t('aiSimilar.title', 'Ähnliche Vorfälle')}
      icon={<Search size={18} />}
      maxWidth="720px"
      closeDisabled={adopting || lockClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={adopting}>
            {t('common.close', 'Schließen')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={adoptIntoTicket}
            disabled={adopting || loading || !hasAssistantMessage}
          >
            {t('aiSimilar.adopt', 'Ins Ticket übernehmen')}
          </button>
        </>
      )}
    >
      {error && <div className="error-message">{error}</div>}
      <div className="ai-chat-thread" style={{ maxHeight: '45vh', padding: 0 }}>
        {loading && (
          <div className="ai-chat-dots" aria-hidden="true">
            <span className="ai-chat-dot" />
            <span className="ai-chat-dot" />
            <span className="ai-chat-dot" />
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} className={`ai-chat-msg ${m.role === 'user' ? 'user' : 'assistant'}`}>
            {/* #231a: Markdown rendern statt Sternchen anzuzeigen */}
            <MarkdownText>{m.content}</MarkdownText>
          </div>
        ))}
        {followupLoading && (
          <div className="ai-chat-dots" aria-hidden="true">
            <span className="ai-chat-dot" />
            <span className="ai-chat-dot" />
            <span className="ai-chat-dot" />
          </div>
        )}
        <div ref={threadEndRef} />
      </div>

      {cards.length > 0 && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <h4 className="ai-search-cards-title">{t('aiSimilar.similarTickets', 'Gefundene Tickets')}</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
            {cards.map(c => (
              <div
                key={c.ticketId}
                className="ai-search-history-row"
                onClick={() => openSimilarTicket(c.ticketId)}
              >
                <div className="ai-search-history-main">
                  <div className="ai-search-history-title">
                    <TicketIcon size={13} style={{ verticalAlign: 'text-top', marginRight: 4 }} />
                    {c.title || `#${c.ticketNumber}`}
                  </div>
                  <div className="ai-search-history-meta">#{c.ticketNumber}</div>
                  {/* #231b: Begründungssatz des Rerankers */}
                  {c.reason && <div className="ai-search-history-reason">{c.reason}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && hasAssistantMessage && contextText && (
        <form className="ai-chat-composer" style={{ paddingLeft: 0, paddingRight: 0 }} onSubmit={sendFollowup}>
          <input
            type="text"
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            placeholder={t('search.ai.followup.placeholder', 'Rückfrage stellen…')}
            disabled={followupLoading}
          />
          <button type="submit" className="btn btn-primary" disabled={followupLoading || !composer.trim()}>
            {t('search.send', 'Senden')}
          </button>
        </form>
      )}
    </Modal>
  );
};

export default TicketAiSearchModal;
